import React from 'react';
import { Slot } from '@radix-ui/react-slot';
import { motion } from 'framer-motion';
import { cn } from '../../lib/cn';

type ButtonVariant = 'primary' | 'secondary' | 'ghost' | 'danger';
type ButtonSize = 'sm' | 'md' | 'lg';

interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  asChild?: boolean;
  isLoading?: boolean;
}

const variantClasses: Record<ButtonVariant, string> = {
  primary:
    'bg-cime-primary text-white hover:bg-[#02241c] focus-visible:ring-cime-primary',
  secondary:
    'border border-cime-primary/25 text-cime-primary bg-transparent hover:border-cime-primary hover:bg-cime-primary/5 focus-visible:ring-cime-primary',
  ghost:
    'bg-transparent text-neutral-700 hover:bg-cime-primary/5 hover:text-cime-primary focus-visible:ring-neutral-400',
  danger:
    'border border-cime-urgent-border text-cime-urgent bg-transparent hover:bg-cime-urgent hover:text-white focus-visible:ring-cime-urgent',
};

const sizeClasses: Record<ButtonSize, string> = {
  // Tailles de la maquette : 30 px (petit) et 36 px (courant).
  sm: 'h-[30px] px-[11px] text-xs rounded-full',
  md: 'h-9 px-3.5 text-[13px] rounded-full',
  lg: 'h-12 px-6 text-base rounded-full',
};

const MotionButton = motion.button;

export const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      variant = 'primary',
      size = 'md',
      asChild = false,
      isLoading = false,
      className,
      disabled,
      children,
      ...props
    },
    ref,
  ) => {
    const baseClass = cn(
      'inline-flex items-center justify-center gap-2 font-medium transition-colors',
      'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2',
      'disabled:opacity-50 disabled:cursor-not-allowed',
      variantClasses[variant],
      sizeClasses[size],
      className,
    );

    if (asChild) {
      return (
        <Slot ref={ref} className={baseClass} {...(props as React.HTMLAttributes<HTMLElement>)}>
          {children}
        </Slot>
      );
    }

    const isDisabled = disabled || isLoading;

    return (
      <MotionButton
        ref={ref}
        disabled={isDisabled}
        className={baseClass}
        whileTap={isDisabled ? undefined : { scale: 0.97 }}
        transition={{ duration: 0.1 }}
        {...(props as React.ComponentProps<typeof MotionButton>)}
      >
        {isLoading ? (
          <span
            className="w-4 h-4 rounded-full border-2 border-current border-t-transparent animate-spin"
            aria-hidden="true"
          />
        ) : null}
        {children}
      </MotionButton>
    );
  },
);

Button.displayName = 'Button';
